// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  SUPPORT_FALLBACK_ERROR_MESSAGE,
  getProviderRejection,
  getUserFacingErrorMessage,
  handleAPIError,
} from '@/api/error-utils';
import { Warehouse as WarehouseIcon } from '@/constants/hardfin-standard-icons';
import { sendPurchaseOrderTo3PL } from '@/purchase-order/api/send-to-3pl';
import { PurchaseOrder } from '@/purchase-order/api/types';
import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { captureException } from '@sentry/react';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

const UNHANDLED_3PL_ERROR_MESSAGE =
  "We're sorry but your 3PL's connection failed. Hardfin support has been automatically notified to investigate";

interface Props {
  isOpen: boolean;
  purchaseOrderKey: string;
  poNumber: string;
  onClose: () => void;
  onSuccess: (updatedPurchaseOrder: PurchaseOrder) => void;
}

/**
 * Dialog component for sending a purchase order to a 3PL for receiving.
 * Once sent, the purchase order will be locked from editing.
 */
export function PurchaseOrderSendTo3PLDialog(props: Props) {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [errorTitle, setErrorTitle] = useState<string | null>(null);

  const sendTo3PLMutation = useMutation({
    mutationFn: () => sendPurchaseOrderTo3PL(props.purchaseOrderKey),
    onSuccess: (updatedPurchaseOrder) => {
      close();
      props.onSuccess(updatedPurchaseOrder);
    },
    onError: (error) => {
      const apiError = handleAPIError(error);
      const rejection = getProviderRejection(apiError.providerErrors);
      if (rejection !== undefined) {
        setErrorTitle(rejection.title);
        setErrorMessage(rejection.body);
        return;
      }
      setErrorTitle(null);
      const curated = getUserFacingErrorMessage(apiError);
      if (curated === SUPPORT_FALLBACK_ERROR_MESSAGE) {
        if (!apiError.sentToSentry) {
          captureException(error);
        }
        setErrorMessage(UNHANDLED_3PL_ERROR_MESSAGE);
      } else {
        setErrorMessage(curated);
      }
    },
  });

  const close = () => {
    props.onClose();
    setErrorMessage(null);
    setErrorTitle(null);
  };

  const handleSubmit = () => {
    sendTo3PLMutation.mutate();
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={props.isOpen} onClose={close}>
      <DialogTitle
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          pb: 2,
        }}
      >
        Send to 3PL
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        <Alert
          severity="info"
          icon={<WarehouseIcon fontSize="medium" />}
          sx={{
            marginY: 3,
            alignItems: 'center',
          }}
        >
          <AlertTitle variant="h6">
            Send order <strong>{props.poNumber}</strong> to 3PL for receiving?
          </AlertTitle>
          <Typography variant="body1">
            Once sent, this order will be locked from editing. The 3PL will
            track incoming inventory and create inbound shipments automatically.
          </Typography>
        </Alert>

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 3, whiteSpace: 'pre-line' }}>
            {errorTitle && <AlertTitle>{errorTitle}</AlertTitle>}
            {errorMessage}
          </Alert>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          py: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Button onClick={close} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="contained"
          disabled={sendTo3PLMutation.isPending}
          sx={{ ml: 1.5 }}
        >
          Send to 3PL
        </Button>
      </DialogActions>
    </Dialog>
  );
}
